"use strict";
import _ from "lodash";
import { SID_LOOKUP } from "./app.constants";
import { homescreenState } from "./base.routes";
import {
    getContactWorkerDisplayName,
    getLatestMembership,
    lazyLoad,
    mapUserWithGrowerMembershipName,
} from "./common/common/common.utils";
import { ACTIVE } from "./common/communication/constants";

export const crewState = {
    name: "app.crew",
    url: "/crews/{crew_sid}",
    lazyLoad: function ($transition$) {
        return Promise.all([
            import(/* webpackChunkName: "grower" */ "./grower"),
        ]).then(function (modules) {
            $transition$
                .injector()
                .get("$ocLazyLoad")
                .load(_.map(modules, "default"));
        });
    },
    resolve: {
        crew: function (GroupService, $transition$) {
            "ngInject";
            return GroupService.retrieve($transition$.params().crew_sid);
        },
        farms: function (FarmService, crew) {
            "ngInject";
            return FarmService.list({
                grower_organization__sid: crew.grower_organization,
            });
        },
        grower: function (GrowerOrganizationService, crew) {
            "ngInject";
            return GrowerOrganizationService.retrieve(crew.grower_organization);
        },
    },
    redirectTo: homescreenState.name,
    data: {},
};

export const crewMessagesState = {
    name: "app.crew.messages",
    url: "/messages?state?owner",
    component: "conversationList",
    resolve: {
        listState: function () {
            "ngInject";
            return crewMessagesState;
        },
        conversationState: function () {
            "ngInject";
            return crewConversationState;
        },
        createConversationState: function () {
            "ngInject";
            return crewConversationCreateState;
        },
        listStateParams: function ($transition$) {
            "ngInject";
            return { crew_sid: $transition$.params().crew_sid };
        },
        createStateParams: function ($transition$) {
            "ngInject";
            return { crew_sid: $transition$.params().crew_sid };
        },
        conversationStateParams: function ($transition$) {
            "ngInject";
            return { crew_sid: $transition$.params().crew_sid };
        },
        listTabs: function (account) {
            "ngInject";
            return [
                {
                    labelKey: "conversationList.MINE_TAB",
                    params: { state: ACTIVE, owner: account.sid },
                },
                {
                    labelKey: "conversationList.UNASSIGNED_TAB",
                    params: { state: ACTIVE, owner: "unassigned" },
                },
            ];
        },
        listLayout: function () {
            "ngInject";
            return "compact";
        },
        conversations: function (ConversationService, $transition$, crew) {
            "ngInject";
            let params = {
                grower_organization__sid: crew.grower_organization,
                state: ACTIVE,
                contact__group__sid: $transition$.params().crew_sid,
            };
            let ownerParamValue = $transition$.params().owner;
            if (ownerParamValue === "unassigned") {
                params.owner__isnull = true;
            } else {
                params.owner__sid = ownerParamValue;
            }
            return ConversationService.list(params);
        },
        growerMemberships: function (
            GrowerMembershipService,
            conversations,
            crew,
        ) {
            "ngInject";
            let ownerSids = _.map(conversations, "owner");
            let contactSids = _.map(conversations, "contact");
            let userSids = _.uniq(_.compact(_.concat(ownerSids, contactSids)));
            return GrowerMembershipService.severalByLookup(
                userSids,
                "user__sid__in",
                {
                    grower_organization__sid: crew.grower_organization,
                },
            );
        },
    },
    data: {
        title: "routes.CREW_MESSAGE_TITLE",
        mixpanelEvent: "Viewed crew messages",
        breadcrumb: "crumbs.CREW_MESSAGE",
    },
};

export const crewConversationState = {
    name: "app.crew.messages.conversation",
    url: "/{conversation_sid}",
    component: "conversation",
    resolve: {
        listState: function () {
            "ngInject";
            return crewMessagesState;
        },
        listStateParams: function (account, crew) {
            "ngInject";
            return {
                crew_sid: crew.sid,
                owner: account.sid,
                state: ACTIVE,
            };
        },
        editState: function () {
            "ngInject";
            return null;
        },
        conversation: function (ConversationService, $transition$) {
            "ngInject";
            return ConversationService.retrieve(
                $transition$.params().conversation_sid,
            );
        },
        addedParticipant: function (conversation) {
            "ngInject";
            return conversation.addParticipant();
        },
        ownerSid: function (conversation) {
            "ngInject";
            return conversation.owner || null;
        },
        ownerRaw: function (AccountService, ownerSid) {
            "ngInject";
            if (!ownerSid) {
                return null;
            }
            return AccountService.retrieve(ownerSid);
        },
        ownerGrowerMembership: function (
            GrowerMembershipService,
            ownerSid,
            grower,
        ) {
            "ngInject";
            if (!ownerSid) {
                return null;
            }
            return GrowerMembershipService.first({
                grower_organization__sid: grower.sid,
                user__sid: ownerSid,
            }).catch(function () {
                return null;
            });
        },
        owner: function (ownerRaw, ownerGrowerMembership) {
            "ngInject";
            return mapUserWithGrowerMembershipName(
                ownerRaw,
                ownerGrowerMembership,
            );
        },
        messages: function () {
            "ngInject";
            return [];
        },
        contactSid: function (conversation) {
            "ngInject";
            return conversation.contact || null;
        },
        contactRaw: function (AccountService, contactSid) {
            "ngInject";
            if (!contactSid) {
                return null;
            }
            return AccountService.retrieve(contactSid);
        },
        contactGrowerMemberships: function (
            GrowerMembershipService,
            contactSid,
            grower,
        ) {
            "ngInject";
            if (!contactSid) {
                return [];
            }
            return GrowerMembershipService.list({
                grower_organization__sid: grower.sid,
                user__sid: contactSid,
            });
        },
        contactGrowerMembership: function (
            contactGrowerMemberships,
            contactSid,
        ) {
            "ngInject";
            return getLatestMembership(contactGrowerMemberships, contactSid);
        },
        contactWorker: function (
            conversation,
            contactRaw,
            contactGrowerMembership,
        ) {
            "ngInject";
            return {
                user: mapUserWithGrowerMembershipName(
                    contactRaw,
                    contactGrowerMembership,
                ),
                growerMembership: contactGrowerMembership,
                displayName: getContactWorkerDisplayName(
                    conversation,
                    contactGrowerMembership,
                ),
            };
        },
        newMessage: function () {
            "ngInject";
            return {};
        },
    },
    data: {
        title: "routes.CREW_CONVERSATION_TITLE",
        mixpanelEvent: "Viewed conversation",
        breadcrumb: "crumbs.CREW_CONVERSATION",
    },
};

export const crewConversationCreateState = {
    name: "app.crew.messages.create",
    url: "/create",
    component: "conversationCreate",
    resolve: {
        conversationState: function () {
            "ngInject";
            return crewConversationState;
        },
        conversationStateParams: function ($transition$) {
            "ngInject";
            return { crew_sid: $transition$.params().crew_sid };
        },
        growerMemberships: function (
            GrowerMembershipService,
            grower,
            $transition$,
        ) {
            "ngInject";
            return GrowerMembershipService.list({
                grower_organization__sid: grower.sid,
                user__group__sid: $transition$.params().crew_sid,
                active: true,
            });
        },
        employees: function (AccountService, $transition$) {
            "ngInject";
            return AccountService.list({
                group__sid: $transition$.params().crew_sid,
            });
        },
        workers: function (employees, growerMemberships) {
            "ngInject";
            return _.compact(
                _.map(employees, function (user) {
                    return getLatestMembership(growerMemberships, user.sid);
                }),
            );
        },
        conversation: function () {
            "ngInject";
            return {};
        },
    },
    data: {
        title: "routes.CREW_CONVERSATION_CREATE_TITLE",
        mixpanelEvent: "Viewed create conversation",
        breadcrumb: "crumbs.CREW_CONVERSATION_CREATE",
    },
};

export const crewAccidentReportBaseState = {
    name: "app.crew.accidentReport",
    abstract: true,
    url: "/report-accident",
    component: "crewAccidentReportBase",
};

export const crewViewAccidentReportState = {
    name: "app.crew.accidentReport.view",
    url: "/{report_sid}",
    component: "accidentReport",
    resolve: {
        report: function (AccidentReportService, $transition$) {
            "ngInject";
            return AccidentReportService.retrieve(
                $transition$.params().report_sid,
            );
        },
        translations: function (
            AccidentReportTranslatedService,
            $translate,
            $transition$,
        ) {
            "ngInject";
            return AccidentReportTranslatedService.list({
                report__sid: $transition$.params().report_sid,
            }).then(function (translations) {
                return {
                    display: _.find(translations, [
                        "language",
                        $translate.use(),
                    ]),
                    original: _.find(translations, "original_language"),
                };
            });
        },
        employeeGrowerMembership: function (
            GrowerMembershipService,
            report,
            grower,
        ) {
            "ngInject";
            return GrowerMembershipService.first({
                grower_organization__sid: grower.sid,
                user__sid: report.employee,
            }).catch(function () {
                return null;
            });
        },
        employee: function (employeeGrowerMembership) {
            "ngInject";
            return employeeGrowerMembership;
        },
        authorRaw: function (AccountService, report) {
            "ngInject";
            return AccountService.retrieve(report.author);
        },
        authorGrowerMembership: function (
            GrowerMembershipService,
            report,
            grower,
        ) {
            "ngInject";
            return GrowerMembershipService.first({
                grower_organization__sid: grower.sid,
                user__sid: report.author,
            }).catch(function () {
                return null;
            });
        },
        author: function (authorRaw, authorGrowerMembership) {
            "ngInject";
            return mapUserWithGrowerMembershipName(
                authorRaw,
                authorGrowerMembership,
            );
        },
        editState: function () {
            "ngInject";
            return crewAccidentReportMetaInformationState;
        },
        archiveRedirectState: function () {
            "ngInject";
            return homescreenState;
        },
    },
    data: {
        title: "routes.VIEW_ACCIDENT_REPORT_TITLE",
        mixpanelEvent: "Viewed accident report",
        breadcrumb: "crumbs.VIEW_ACCIDENT_REPORT",
    },
};

export const crewAccidentReportEditBaseState = {
    name: "app.crew.accidentReport.view.base",
    abstract: true,
    component: "accidentReportEditBase",
    redirectTo: crewViewAccidentReportState,
};

export const crewAccidentReportMetaInformationState = {
    name: "app.crew.accidentReport.view.base.metaInformation",
    url: "/basic-information",
    component: "accidentReportMetaInformation",
    resolve: {
        prefillData: function (report) {
            "ngInject";
            return report.prefill();
        },
        nextState: function () {
            "ngInject";
            return crewAccidentReportEmployeeInformationState;
        },
    },
    data: {
        title: "routes.ACCIDENT_REPORT_META_INFORMATION_TITLE",
        mixpanelEvent: "Viewed accident report basic information",
    },
};

export const crewAccidentReportEmployeeInformationState = {
    name: "app.crew.accidentReport.view.base.employeeInformation",
    url: "/employee-information",
    component: "accidentReportEmployeeInformation",
    resolve: {
        prefillData: function (report) {
            "ngInject";
            return report.prefill();
        },
        nextState: function () {
            "ngInject";
            return crewAccidentReportAccidentInformationState;
        },
        reloadState: function () {
            "ngInject";
            return crewViewAccidentReportState;
        },
    },
    data: {
        title: "routes.ACCIDENT_REPORT_EMPLOYEE_INFORMATION_TITLE",
        mixpanelEvent: "Viewed accident report employee information",
    },
};

export const crewAccidentReportAccidentInformationState = {
    name: "app.crew.accidentReport.view.base.accidentInformation",
    url: "/accident-information",
    component: "accidentReportAccidentInformation",
    resolve: {
        nextState: function () {
            "ngInject";
            return crewAccidentReportHealthCareInformationState;
        },
        reloadState: function () {
            "ngInject";
            return crewViewAccidentReportState;
        },
    },
    data: {
        title: "routes.ACCIDENT_REPORT_ACCIDENT_INFORMATION_TITLE",
        mixpanelEvent: "Viewed accident report accident information",
    },
};

export const crewAccidentReportHealthCareInformationState = {
    name: "app.crew.accidentReport.view.base.healthcareInformation",
    url: "/healthcare-information",
    component: "accidentReportHealthCareInformation",
    resolve: {
        nextState: function () {
            "ngInject";
            return crewViewAccidentReportState;
        },
        reloadState: function () {
            "ngInject";
            return crewViewAccidentReportState;
        },
    },
    data: {
        title: "routes.ACCIDENT_REPORT_HEALTH_CARE_INFORMATION_TITLE",
        mixpanelEvent: "Viewed accident report health care information",
    },
};

export const crewCreateAccidentReportState = {
    name: "app.crew.accidentReport.create",
    url: "",
    component: "createAccidentReport",
    resolve: {
        growerMemberships: function (
            GrowerMembershipService,
            grower,
            $transition$,
        ) {
            "ngInject";
            return GrowerMembershipService.list({
                grower_organization__sid: grower.sid,
                user__group__sid: $transition$.params().crew_sid,
                active: true,
            });
        },
        users: function (AccountService, $transition$) {
            "ngInject";
            return AccountService.list({
                group__sid: $transition$.params().crew_sid,
            });
        },
        employees: function (growerMemberships, users) {
            "ngInject";
            return _.compact(
                _.map(users, function (user) {
                    return getLatestMembership(growerMemberships, user.sid);
                }),
            );
        },
        nextState: function () {
            "ngInject";
            return crewAccidentReportMetaInformationState;
        },
    },
    data: {
        title: "routes.CREW_ACCIDENT_REPORT_TITLE",
        mixpanelEvent: "Viewed crew accident report",
        breadcrumb: "crumbs.CREW_ACCIDENT_REPORT",
    },
};
export const crewCreateAnnouncementBaseState = {
    name: "app.crew.send-announcement",
    url: "/{announcement_sid}",
    abstract: true,
    component: "crewAnnouncementBase",
    lazyLoad: function ($transition$) {
        return Promise.all([
            import(
                /* webpackChunkName: "lazy.communication" */ "./lazy/communication"
            ),
        ]).then(function (modules) {
            $transition$
                .injector()
                .get("$ocLazyLoad")
                .load(_.map(modules, "default"));
        });
    },
    resolve: {
        announcement: function (AnnouncementService, $transition$) {
            "ngInject";
            return AnnouncementService.retrieve(
                $transition$.params().announcement_sid,
            );
        },
        crew: function (GroupService, $transition$) {
            "ngInject";
            return GroupService.retrieve($transition$.params().crew_sid);
        },
        deleteRedirectState: function () {
            "ngInject";
            return homescreenState;
        },
        progressStates: function () {
            "ngInject";
            return [
                crewCreateAnnouncementAddRecipientsState,
                crewCreateAnnouncementAddContentsState,
                crewCreateAnnouncementAddAttachmentsState,
                crewCreateAnnouncementAddLocationState,
                crewCreateAnnouncementReviewAnnouncementState,
            ];
        },
    },
};
export const crewCreateAnnouncementAddRecipientsState = {
    name: "app.crew.send-announcement.addRecipients",
    url: "/select-recipients",
    component: "addRecipients",
    resolve: {
        announcement: function (AnnouncementService, $transition$) {
            "ngInject";
            return AnnouncementService.retrieve(
                $transition$.params().announcement_sid,
            );
        },
        nextState: function () {
            "ngInject";
            return crewCreateAnnouncementAddContentsState;
        },
        reloadState: function () {
            "ngInject";
            return crewCreateAnnouncementBaseState;
        },
    },
    data: {
        title: "routes.CREW_CREATE_ANNOUNCEMENT_ADD_RECIPIENTS_TITLE",
        mixpanelEvent: "Viewed new crew announcement add recipients",
    },
};
export const crewCreateAnnouncementAddContentsState = {
    name: "app.crew.send-announcement.addContents",
    url: "/write-message",
    component: "addContents",
    resolve: {
        announcementTranslateds: function (
            AnnouncementTRANSLATEDService,
            $transition$,
        ) {
            "ngInject";
            return AnnouncementTRANSLATEDService.list({
                announcement__sid: $transition$.params().announcement_sid,
            });
        },
        previousState: function () {
            "ngInject";
            return crewCreateAnnouncementAddRecipientsState;
        },
        nextState: function () {
            "ngInject";
            return crewCreateAnnouncementAddAttachmentsState;
        },
        reloadState: function () {
            "ngInject";
            return crewCreateAnnouncementBaseState;
        },
    },
    data: {
        title: "routes.CREW_CREATE_ANNOUNCEMENT_ADD_CONTENTS_TITLE",
        mixpanelEvent: "Viewed new crew announcement write message",
    },
};
export const crewCreateAnnouncementAddAttachmentsState = {
    name: "app.crew.send-announcement.addAttachments",
    url: "/add-attachments",
    component: "addAttachments",
    resolve: {
        announcement: function (AnnouncementService, $transition$) {
            "ngInject";
            return AnnouncementService.retrieve(
                $transition$.params().announcement_sid,
            );
        },
        currentAttachments: function (AttachmentService, $transition$) {
            "ngInject";
            return AttachmentService.list({
                announcement__sid: $transition$.params().announcement_sid,
            });
        },
        previousState: function () {
            "ngInject";
            return crewCreateAnnouncementAddContentsState;
        },
        nextState: function () {
            "ngInject";
            return crewCreateAnnouncementAddLocationState;
        },
        reloadState: function () {
            "ngInject";
            return crewCreateAnnouncementBaseState;
        },
    },
    data: {
        title: "routes.CREW_CREATE_ANNOUNCEMENT_ADD_ATTACHMENTS_TITLE",
        mixpanelEvent: "Viewed new crew announcement add attachments",
    },
};
export const crewCreateAnnouncementAddLocationState = {
    name: "app.crew.send-announcement.addLocation",
    url: "/add-location",
    component: "addLocation",
    resolve: {
        announcement: function (AnnouncementService, $transition$) {
            "ngInject";
            return AnnouncementService.retrieve(
                $transition$.params().announcement_sid,
            );
        },
        previousState: function () {
            "ngInject";
            return crewCreateAnnouncementAddAttachmentsState;
        },
        nextState: function () {
            "ngInject";
            return crewCreateAnnouncementReviewAnnouncementState;
        },
        reloadState: function () {
            "ngInject";
            return crewCreateAnnouncementBaseState;
        },
    },
    data: {
        title: "routes.CREW_CREATE_ANNOUNCEMENT_ADD_LOCATION_TITLE",
        mixpanelEvent: "Viewed new crew announcement add location",
    },
};
export const crewCreateAnnouncementReviewAnnouncementState = {
    name: "app.crew.send-announcement.reviewAnnouncement",
    url: "/review",
    component: "reviewAnnouncement",
    resolve: {
        announcement: function (AnnouncementService, $transition$) {
            "ngInject";
            return AnnouncementService.retrieve(
                $transition$.params().announcement_sid,
                {},
                {
                    expand: ["translations"],
                },
            );
        },
        senderSid: function (announcement) {
            "ngInject";
            return announcement.sender || null;
        },
        senderRaw: function (AccountService, senderSid) {
            "ngInject";
            if (!senderSid) {
                return null;
            }
            return AccountService.retrieve(senderSid);
        },
        senderGrowerMembership: function (
            GrowerMembershipService,
            senderSid,
            grower,
        ) {
            "ngInject";
            if (!senderSid) {
                return null;
            }
            return GrowerMembershipService.first({
                grower_organization__sid: grower.sid,
                user__sid: senderSid,
            }).catch(function () {
                return null;
            });
        },
        sender: function (senderRaw, senderGrowerMembership) {
            "ngInject";
            return mapUserWithGrowerMembershipName(
                senderRaw,
                senderGrowerMembership,
            );
        },
        recipients: function ($transition$, AccountService) {
            "ngInject";
            return AccountService.list({
                announcement__sid: $transition$.params().announcement_sid,
            });
        },
        attachments: function ($transition$, AttachmentService) {
            "ngInject";
            return AttachmentService.list({
                announcement__sid: $transition$.params().announcement_sid,
            }).then(function (results) {
                return _.groupBy(results, "language");
            });
        },
        previousState: function () {
            "ngInject";
            return crewCreateAnnouncementAddLocationState;
        },
        nextState: function () {
            "ngInject";
            return homescreenState;
        },
    },
    data: {
        title: "routes.CREW_CREATE_ANNOUNCEMENT_REVIEW_ANNOUNCEMENT_TITLE",
        mixpanelEvent: "Viewed new crew announcement review",
    },
};
export const crewDisciplineReportBaseState = {
    name: "app.crewDisciplineReport",
    url: "/manager/{grower_sid}/record-employee-issue/{report_sid}",
    abstract: true,
    lazyLoad: function ($transition$) {
        return Promise.all([
            import(
                /* webpackChunkName: "lazy.discipline" */ "./lazy/discipline"
            ),
        ]).then(function (modules) {
            $transition$
                .injector()
                .get("$ocLazyLoad")
                .load(_.map(modules, "default"));
        });
    },
    resolve: {
        grower: function ($transition$, GrowerOrganizationService) {
            "ngInject";
            return GrowerOrganizationService.retrieve(
                $transition$.params().grower_sid,
            );
        },
    },
};
export const crewAddDetailsDisciplineReportState = {
    name: "app.crewDisciplineReport.addDetails",
    url: "/add-details",
    component: "addDetailsDisciplineReport",
    resolve: {
        report: function ($transition$, DisciplineReportService) {
            "ngInject";
            return DisciplineReportService.retrieve(
                $transition$.params().report_sid,
            );
        },
        growerMemberships: function (GrowerMembershipService, $transition$) {
            "ngInject";
            return GrowerMembershipService.list({
                grower_organization__sid: $transition$.params().grower_sid,
                active: true,
            });
        },
        users: function (AccountService, $transition$) {
            "ngInject";
            return AccountService.list({
                growermembership__grower_organization__sid:
                    $transition$.params().grower_sid,
            });
        },
        workers: function (users, growerMemberships) {
            "ngInject";
            return growerMemberships;
        },
    },
    data: {
        title: "routes.CREW_ADD_DETAILS_DISCIPLINE_REPORT_TITLE",
        mixpanelEvent: "Viewed crew add details discipline report",
    },
};
export const crewInstructionsDisciplineReportState = {
    name: "app.crewDisciplineReport.instructions",
    url: "/instructions",
    component: "instructionsDisciplineReport",
    resolve: {
        report: function ($transition$, DisciplineReportService) {
            "ngInject";
            return DisciplineReportService.retrieve(
                $transition$.params().report_sid,
            );
        },
    },
    data: {
        title: "routes.CREW_INSTRUCTION_DISCIPLINE_REPORT_TITLE",
        mixpanelEvent: "Viewed crew instructions discipline report",
    },
};
export const crewAddEmployeeStatementDisciplineReportState = {
    name: "app.crewDisciplineReport.addEmployeeStatement",
    url: "/add-employee-statement",
    component: "addEmployeeStatementDisciplineReport",
    resolve: {
        report: function ($transition$, DisciplineReportService) {
            "ngInject";
            return DisciplineReportService.retrieve(
                $transition$.params().report_sid,
            );
        },
    },
    data: {
        title: "routes.CREW_ADD_EMPLOYEE_STATEMENT_DISCIPLINE_REPORT_TITLE",
        mixpanelEvent: "Viewed crew add employee statement discipline report",
    },
};
export const crewAwaitApprovalDisciplineReportState = {
    name: "app.crewDisciplineReport.awaitApproval",
    url: "/wait-for-approval",
    component: "awaitApprovalDisciplineReport",
    resolve: {
        report: function ($transition$, DisciplineReportService) {
            "ngInject";
            return DisciplineReportService.retrieve(
                $transition$.params().report_sid,
            );
        },
    },
    data: {
        title: "routes.CREW_AWAIT_APPROVAL_DISCIPLINE_REPORT_TITLE",
        mixpanelEvent: "Viewed crew await approval discipline report",
    },
};
export const crewRecommendedActionDisciplineReportState = {
    name: "app.crewDisciplineReport.recommendedAction",
    url: "/recommended-action",
    component: "recommendedActionDisciplineReport",
    resolve: {
        report: function ($transition$, DisciplineReportService) {
            "ngInject";
            return DisciplineReportService.retrieve(
                $transition$.params().report_sid,
            );
        },
        subjectEmployee: function (GrowerMembershipService, report) {
            "ngInject";
            return GrowerMembershipService.retrieve(report.subject_employee);
        },
        translated: function (
            $transition$,
            DisciplineReportTranslatedService,
            $translate,
        ) {
            "ngInject";
            return DisciplineReportTranslatedService.first({
                discipline_report__sid: $transition$.params().report_sid,
                language: $translate.use(),
            });
        },
        myGrowerMembership: function (
            account,
            GrowerMembershipService,
            $transition$,
        ) {
            "ngInject";
            return GrowerMembershipService.first({
                user__sid: account.sid,
                grower_organization__sid: $transition$.params().grower_sid,
            });
        },
    },
    data: {
        title: "routes.CREW_RECOMMENDED_ACTION_DISCIPLINE_REPORT_TITLE",
        mixpanelEvent: "Viewed crew recommended action discipline report",
    },
};
export const crewAddOwnActionDisciplineReportState = {
    name: "app.crewDisciplineReport.recommendedAction.edit",
    url: "/edit",
    component: "addOwnActionDisciplineReport",
    resolve: {
        report: function ($transition$, DisciplineReportService) {
            "ngInject";
            return DisciplineReportService.retrieve(
                $transition$.params().report_sid,
            );
        },
        myGrowerMembership: function (
            account,
            GrowerMembershipService,
            $transition$,
        ) {
            "ngInject";
            return GrowerMembershipService.first({
                user__sid: account.sid,
                grower_organization__sid: $transition$.params().grower_sid,
            });
        },
    },
    data: {
        title: "routes.CREW_ADD_OWN_ACTION_DISCIPLINE_REPORT_TITLE",
        mixpanelEvent: "Viewed crew add own action discipline report",
    },
};
export const crewExecuteActionDisciplineReportState = {
    name: "app.crewDisciplineReport.executeAction",
    url: "/action",
    component: "executeActionDisciplineReport",
    resolve: {
        report: function ($transition$, DisciplineReportService) {
            "ngInject";
            return DisciplineReportService.retrieve(
                $transition$.params().report_sid,
            );
        },
        subjectEmployee: function (GrowerMembershipService, report) {
            "ngInject";
            return GrowerMembershipService.retrieve(report.subject_employee);
        },
        translated: function (
            $transition$,
            DisciplineReportTranslatedService,
            $translate,
        ) {
            "ngInject";
            return DisciplineReportTranslatedService.first({
                discipline_report__sid: $transition$.params().report_sid,
                language: $translate.use(),
            });
        },
        myGrowerMembership: function (
            account,
            GrowerMembershipService,
            $transition$,
        ) {
            "ngInject";
            return GrowerMembershipService.first({
                user__sid: account.sid,
                grower_organization__sid: $transition$.params().grower_sid,
            });
        },
    },
    data: {
        title: "routes.CREW_EXECUTE_ACTION_DISCIPLINE_REPORT_TITLE",
        mixpanelEvent: "Viewed crew execute action discipline report",
    },
};
export const crewDoneDisciplineReportState = {
    name: "app.crewDisciplineReport.done",
    url: "/done",
    component: "doneDisciplineReport",
    resolve: {
        report: function ($transition$, DisciplineReportService) {
            "ngInject";
            return DisciplineReportService.retrieve(
                $transition$.params().report_sid,
            );
        },
    },
    data: {
        title: "routes.CREW_DONE_DISCIPLINE_REPORT_TITLE",
        mixpanelEvent: "Viewed crew done discipline report",
    },
};
